import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AuthService, CartOrderItem, Order, ProductAttribute, ShopAddress } from '../auth.service';
import { CartService } from '../cart.service';
import { forkJoin } from 'rxjs';
import { Router, RouterModule } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';

@Component({
  selector: 'app-cart',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './cart.component.html',
  styleUrl: './cart.component.css'
})
export class CartComponent implements OnInit {
  cartItems: CartOrderItem[] = [];
  userId!: number;
  warningMessages: { [key: string]: string } = {};
  productAttributes: ProductAttribute[] = []; // Stockez vos attributs de produits ici
  hasActiveAddress: boolean = false;

  constructor(private cartService: CartService, public authService: AuthService, private router: Router, private meta: Meta,private titleService: Title) { }

  ngOnInit(): void {
    this.userId = this.getCurrentUserId();

    // Souscrire aux éléments du panier
    this.cartService.cartItems$.subscribe(items => {
      this.cartItems = items || [];
      this.reveal();
    });
    this.checkActiveAddress();

    // Chargez les attributs de produits
    this.loadProductAttributes();
    this.reveal()
  }

  private checkActiveAddress(): void {
    this.authService.getShopAddressesByUserId(this.userId).subscribe((addresses: ShopAddress[]) => {
      this.hasActiveAddress = addresses.some(address => address.status);
    });
  }

  private updateMetaTags(): void {
    const totalItems = this.cartItems.length;
    const totalAmount = this.getTotalAmount();

    this.titleService.setTitle(`Your Cart - Cognitiex`);
    this.meta.addTags([
      { name: 'description', content: `You have ${totalItems} items in your cart with a total amount of ${totalAmount}$. Complete your purchase now!` },
      { name: 'keywords', content: 'Cart, online shopping, order, products, Cognitiex' },
      { name: 'author', content: 'Cognitiex' },
      { property: 'og:title', content: 'Your Cart - Cognitiex' },
      { property: 'og:description', content: `Items in your cart: ${totalItems}. Total amount: ${totalAmount}$. Check out our products now.` },
      { property: 'og:image', content: '/assets/images/cart-preview.png' },
      { property: 'og:url', content: `https://www.cognitiex.com/cart` },
      { property: 'og:type', content: 'website' },
      { property: 'twitter:card', content: 'summary_large_image' },
      { property: 'twitter:title', content: 'Your Cart - Cognitiex' },
      { property: 'twitter:description', content: `You have ${totalItems} items in your cart with a total amount of ${totalAmount}$.` },
      { name: 'robots', content: 'index, follow' }
    ]);
  }


  private getCurrentUserId(): number {
    return 1; // Placeholder pour l'ID utilisateur réel
  }

  private loadProductAttributes(): void {
    this.authService.getProductAttribute().subscribe((attributes: ProductAttribute[]) => {
      this.productAttributes = attributes; // Stockez les attributs de produits
    });
  }

  public isEligibleForTotal(attributeId: string): boolean {
    const attribute = this.productAttributes.find(attr => attr.id?.toString() === attributeId);
    if (!attribute) {
      console.warn(`Attribute with id ${attributeId} not found`);
      return false;
    }
    // Allow total calculation only if both `status` and `isFeatured` are false
    return attribute.product.status === false && attribute.product.isFeatured === false;
  }



  // Méthode pour récupérer la quantité maximale d'un attribut
  getMaxQuantity(attributeId: string): number {
    const attribute = this.productAttributes.find(attr => attr.id?.toString() === attributeId);
    return attribute ? attribute.qte : 0; // Retourner la quantité disponible
  }

  updateQuantity(attribute: string, quantity: number): void {
    const maxQuantity = this.getMaxQuantity(attribute); // Récupérez la quantité maximale
    if (quantity < 0) { // Limite inférieure basée sur @Min
      this.warningMessages[attribute] = `The requested quantity cannot be less than 0. You can adapt or delete this product.`;
    } else if (quantity < 1) {
      this.removeItem(attribute);
    } else if (quantity > maxQuantity) {
      this.warningMessages[attribute] = `The requested quantity far exceeds our availability (max: ${maxQuantity}). You can adapt or delete this product.`;
    } else {
      this.warningMessages[attribute] = ''; // Réinitialise l'avertissement si la quantité est valide
      this.cartService.updateQuantity(attribute, quantity);
    }
  }

  removeItem(attribute: string): void {
    this.cartService.removeItem(attribute);
    delete this.warningMessages[attribute]; // Supprime l'avertissement lié à cet attribut
  }

  getTotalAmount(): number {
    return this.cartItems.reduce((acc, item) => {
      const itemPrice = this.isEligibleForTotal(item.attribute) ? item.price : 0;
      return acc + (itemPrice * item.quantity);
    }, 0);
  }


  getTotalItemCount(): number {
    return this.cartService.getTotalItemCount();
  }

  onQuantityChange(event: Event, item: CartOrderItem): void {
    const target = event.target as HTMLInputElement;
    const newQuantity = Number(target.value);
    if (!isNaN(newQuantity)) {
      this.updateQuantity(item.attribute, newQuantity); // Met à jour la quantité
    } else {
      console.error('Invalid quantity:', target.value);
    }
  }

  finalizeOrder(): void {
    if (!this.hasActiveAddress) {
      alert("You must create an address before finalizing the order.");
      return;
    }
    if (this.cartItems.length === 0) {
      alert('Your shopping cart is empty.');
      return;
    }

    const itemsToSend: CartOrderItem[] = this.cartItems.map(item => ({
      item: item.item,
      imagePath: item.imagePath,
      attribute: item.attribute,
      quantity: item.quantity,
      price: item.price || 0,
      totalAmt: (item.price || 0) * item.quantity
    }));

    console.log('Items to send:', itemsToSend);

    this.authService.createOrder(itemsToSend).subscribe(
      (response: any) => {
        this.cartService.clearCart();
        alert('Order completed successfully.');
      },
      (error: any) => {
        console.error('Erreur lors de la finalisation de la commande:', error);
      }
    );
  }

  reveal(): void {
    const reveals = document.querySelectorAll(".reveal");
    const triggerBottom = window.innerHeight / 5 * 4;

    reveals.forEach(box => {
      const boxTop = (box as HTMLElement).getBoundingClientRect().top;
      if (boxTop < triggerBottom) {
        (box as HTMLElement).classList.add('active');
      } else {
        (box as HTMLElement).classList.remove('active');
      }
    });
  }
}
